import React, { useState, useEffect, useCallback } from 'react';
import {
    Truck, Package, CheckCircle, BarChart2,
    Clock, RefreshCw, TrendingUp, TrendingDown, AlertCircle,
    ArrowUpDown, ArrowUp, ArrowDown, Download, Calendar
} from 'lucide-react';
import { getShipmentAnalytics } from '../../../api/crm/shipwayApi';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

// ─── KPI Card ────────────────────────────────────────────────────────────────
const KpiCard = ({ title, value, sub, color = 'text-gray-900', bg = 'bg-white', icon: Icon }) => (
    <div className={`${bg} rounded-xl border border-gray-200 shadow-sm p-5`}>
        <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-500">{title}</span>
            {Icon && <Icon className="h-5 w-5 text-gray-400" />}
        </div>
        <div className={`text-3xl font-bold ${color}`}>{value ?? '—'}</div>
        {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
);

// ─── Courier row ─────────────────────────────────────────────────────────────
const Bar = ({ pct = 0, color = 'bg-indigo-500' }) => (
    <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
                className={`h-full ${color} rounded-full transition-all`}
                style={{ width: `${Math.min(100, pct)}%` }}
            />
        </div>
        <span className="text-xs text-gray-600 w-10 text-right">{pct}%</span>
    </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const ShipmentAnalytics = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filter state
    const [filterPreset, setFilterPreset] = useState('30'); // Default to 30 days
    const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });

    // Courier table sort
    const [sortCol, setSortCol] = useState('volume');
    const [sortDir, setSortDir] = useState('desc');

    const getQueryDates = useCallback(() => {
        if (filterPreset === 'custom') {
            return customRange;
        }

        const now = new Date();
        const end = now.toISOString().split('T')[0];
        let start = new Date();

        switch (filterPreset) {
            case 'today':
                // Same day
                break;
            case '7':
                start.setDate(now.getDate() - 7);
                break;
            case '15':
                start.setDate(now.getDate() - 15);
                break;
            case '30':
                start.setDate(now.getDate() - 30);
                break;
            case '60':
                start.setDate(now.getDate() - 60);
                break;
            case '180':
                start.setMonth(now.getMonth() - 6);
                break;
            case '365':
                start.setFullYear(now.getFullYear() - 1);
                break;
            default:
                return { startDate: '', endDate: '' };
        }

        return {
            startDate: start.toISOString().split('T')[0],
            endDate: end
        };
    }, [filterPreset, customRange]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = getQueryDates();
            const res = await getShipmentAnalytics(params);
            setData(res?.data || res);
        } catch (e) {
            setError(e?.message || 'Failed to load analytics.');
        } finally {
            setLoading(false);
        }
    }, [getQueryDates]);

    useEffect(() => {
        if (filterPreset !== 'custom' || (customRange.startDate && customRange.endDate)) {
            fetchData();
        }
    }, [fetchData, filterPreset, customRange]);

    const exportToCSV = () => {
        if (!data) return;

        const sections = [];

        // 1. Overall Summary Section
        sections.push(['SHIPMENT ANALYTICS SUMMARY']);
        sections.push(['Report Date', new Date().toLocaleString()]);
        sections.push(['Filter', filterPreset === 'custom' ? `${customRange.startDate} to ${customRange.endDate}` : `${filterPreset} days preset`]);
        sections.push([]);

        sections.push(['KPI', 'Value']);
        sections.push(['Total Shipments', data.totalShipments || 0]);
        sections.push(['Delivered', data.delivered || 0]);
        sections.push(['In Transit', data.inTransit || 0]);
        sections.push(['Out for Delivery', data.outForDelivery || 0]);
        sections.push(['RTO / Cancelled', data.rto || 0]);
        sections.push(['Overall Success Rate', `${data.successRate || 0}%`]);
        sections.push(['Avg Delivery Time', data.avgDeliveryTime ? `${data.avgDeliveryTime} days` : 'N/A']);
        sections.push([]);
        sections.push([]);

        // 2. Courier Performance Section
        if (data.courierPerformance?.length) {
            sections.push(['COURIER PERFORMANCE BREAKUP']);
            const headers = ['Courier', 'Volume', 'Delivered', 'Success Rate (%)', 'Avg Days'];
            sections.push(headers);

            data.courierPerformance.forEach(c => {
                sections.push([
                    c.courier,
                    c.volume,
                    c.delivered,
                    c.successRate,
                    c.avgDeliveryDays || 'N/A'
                ]);
            });
        }

        const csvContent = sections
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];
        const timeStr = now.toLocaleTimeString('en-IN', { hour12: false, hour: '2-digit', minute: '2-digit' }).replace(':', '-');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `courier_analytics_${dateStr}_${timeStr}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Sort courier table
    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('desc'); }
    };

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
        return sortDir === 'asc'
            ? <ArrowUp className="h-3.5 w-3.5 text-indigo-600" />
            : <ArrowDown className="h-3.5 w-3.5 text-indigo-600" />;
    };

    const sortedCouriers = () => {
        if (!data?.courierPerformance?.length) return [];
        return [...data.courierPerformance].sort((a, b) => {
            const av = a[sortCol] ?? 0;
            const bv = b[sortCol] ?? 0;
            const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
            return sortDir === 'asc' ? cmp : -cmp;
        });
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6">
            {/* Header & Main Filters */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <BarChart2 className="h-5 w-5 text-indigo-600" />
                        Courier Analytics
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Performance overview based on delivery events
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    {/* Range Presets */}
                    <select
                        value={filterPreset}
                        onChange={(e) => setFilterPreset(e.target.value)}
                        className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer bg-white"
                    >
                        <option value="today">Today</option>
                        <option value="7">Last 7 Days</option>
                        <option value="15">Last 15 Days</option>
                        <option value="30">Last 30 Days</option>
                        <option value="60">Last 60 Days</option>
                        <option value="180">Last 6 Months</option>
                        <option value="365">Last 1 Year</option>
                        <option value="custom">Custom Range</option>
                    </select>

                    {filterPreset === 'custom' && (
                        <DateRangeFilter
                            startDate={customRange.startDate}
                            endDate={customRange.endDate}
                            onChange={setCustomRange}
                        />
                    )}

                    <button
                        onClick={exportToCSV}
                        disabled={!data?.courierPerformance?.length}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition disabled:opacity-50 shadow-sm"
                    >
                        <Download className="h-4 w-4" />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <KpiCard
                    title="Total Shipments"
                    value={loading ? '…' : (data?.totalShipments ?? 0).toLocaleString('en-IN')}
                    icon={Package}
                    color="text-gray-900"
                />
                <KpiCard
                    title="Delivered"
                    value={loading ? '…' : (data?.delivered ?? 0).toLocaleString('en-IN')}
                    icon={CheckCircle}
                    color="text-green-600"
                    bg="bg-green-50"
                />
                <KpiCard
                    title="In Transit"
                    value={loading ? '…' : (data?.inTransit ?? 0).toLocaleString('en-IN')}
                    icon={Truck}
                    color="text-blue-600"
                    bg="bg-blue-50"
                />
                <KpiCard
                    title="Out for Delivery"
                    value={loading ? '…' : (data?.outForDelivery ?? 0).toLocaleString('en-IN')}
                    icon={Clock}
                    color="text-orange-600"
                    bg="bg-orange-50"
                />
                <KpiCard
                    title="RTO / Cancelled"
                    value={loading ? '…' : (data?.rto ?? 0).toLocaleString('en-IN')}
                    icon={AlertCircle}
                    color="text-red-600"
                    bg="bg-red-50"
                />
            </div>

            {/* Success Rate + Avg Delivery */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-500">Success Rate</span>
                        <TrendingUp className="h-4 w-4 text-green-500" />
                    </div>
                    <div className="text-4xl font-bold text-green-600 mb-2">
                        {loading ? '…' : `${data?.successRate ?? 0}%`}
                    </div>
                    {/* Progress bar */}
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-green-500 rounded-full transition-all"
                            style={{ width: `${data?.successRate ?? 0}%` }}
                        />
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">
                        {data?.delivered ?? 0} of {data?.totalShipments ?? 0} shipments delivered
                    </p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-500">Avg Delivery Time</span>
                        <Clock className="h-4 w-4 text-indigo-500" />
                    </div>
                    <div className="text-4xl font-bold text-indigo-600 mb-2">
                        {loading
                            ? '…'
                            : data?.avgDeliveryTime != null
                                ? `${data.avgDeliveryTime}d`
                                : 'N/A'}
                    </div>
                    <p className="text-xs text-gray-400">
                        {data?.avgDeliveryTime != null
                            ? 'Average days from pickup to delivery'
                            : 'Not enough delivered orders to calculate'}
                    </p>
                </div>
            </div>

            {/* Courier Performance Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-800">Courier Performance</h3>
                </div>

                {loading ? (
                    <div className="p-8 text-center">
                        <RefreshCw className="h-6 w-6 text-gray-300 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Loading analytics…</p>
                    </div>
                ) : !data?.courierPerformance?.length ? (
                    <div className="p-8 text-center">
                        <Truck className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No courier data found for the selected period.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {[
                                        { key: 'courier', label: 'Courier' },
                                        { key: 'volume', label: 'Volume' },
                                        { key: 'delivered', label: 'Delivered' },
                                        { key: 'successRate', label: 'Success Rate' },
                                        { key: 'avgDeliveryDays', label: 'Avg Days' },
                                    ].map(({ key, label }) => (
                                        <th
                                            key={key}
                                            onClick={() => handleSort(key)}
                                            className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                                        >
                                            <div className="flex items-center gap-1">
                                                {label}
                                                <SortIcon col={key} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {sortedCouriers().map((c, i) => (
                                    <tr key={c.courier || i} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center">
                                                    <Truck className="h-3.5 w-3.5 text-indigo-600" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-900">{c.courier}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 font-medium">
                                            {c.volume?.toLocaleString('en-IN')}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700 font-medium">
                                            {c.delivered?.toLocaleString('en-IN')}
                                        </td>
                                        <td className="px-4 py-3 min-w-[140px]">
                                            <Bar
                                                pct={c.successRate}
                                                color={c.successRate >= 90 ? 'bg-green-500' : c.successRate >= 75 ? 'bg-yellow-500' : 'bg-red-500'}
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                            {c.avgDeliveryDays != null
                                                ? <span className="font-medium">{c.avgDeliveryDays}d</span>
                                                : <span className="text-gray-300">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShipmentAnalytics;
